import type {
  ClosingChecklist,
  WithdrawalRequest,
} from "../../../domain/types/withdrawal";
import { httpClient } from "../http-client";

interface Envelope<T> {
  data: T;
  errorMsg: string | null;
  errorCode: string | null;
}

/**
 * 사이트 운영 종료(COMPANY_ADMIN) · 회원 탈퇴 API.
 *
 * 종료는 신청 → ADMIN 승인 → 7일 뒤 삭제 순서로 진행되며,
 * 신청 시점부터 쇼핑몰의 신규 주문이 차단된다.
 */
export class CompanyWithdrawalApiService {
  /** 사전 조건 3종 상태 — 카드 뱃지와 신청 버튼 활성화 판정 */
  async getClosingChecklist(companyId: number): Promise<ClosingChecklist> {
    const response = await httpClient.get<Envelope<ClosingChecklist>>(
      `/api/company/${companyId}/closing-checklist`
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "종료 준비 상태를 가져오지 못했습니다."
      );
    }

    return response.data.data;
  }

  /** 내 신청 상태. 신청한 적이 없으면 null */
  async getMyRequest(): Promise<WithdrawalRequest | null> {
    const response = await httpClient.get<Envelope<WithdrawalRequest | null>>(
      "/api/company/withdrawal/request"
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "신청 상태를 가져오지 못했습니다."
      );
    }

    return response.data.data;
  }

  /** 운영 종료 신청 (COMPANY_ADMIN) */
  async requestClosing(reason?: string): Promise<WithdrawalRequest> {
    const response = await httpClient.post<Envelope<WithdrawalRequest>>(
      "/api/company/withdrawal/request",
      { reason: reason || null }
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "종료 신청에 실패했습니다.");
    }

    return response.data.data;
  }

  /** 신청 철회 · 예약 취소 */
  async cancelRequest(): Promise<WithdrawalRequest> {
    const response = await httpClient.delete<Envelope<WithdrawalRequest>>(
      "/api/company/withdrawal/request"
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "철회에 실패했습니다.");
    }

    return response.data.data;
  }

  /** 주문 내역 엑셀 (전체 기간) */
  async exportOrders(companyId: number): Promise<Blob> {
    const response = await httpClient.get<Blob>(
      `/api/company/${companyId}/order/export`,
      { responseType: "blob" }
    );

    return response.data;
  }

  /** 거래처 기업 정보 엑셀 */
  async exportBusinessClients(companyId: number): Promise<Blob> {
    const response = await httpClient.get<Blob>(
      `/api/company/${companyId}/business-user/export`,
      { responseType: "blob" }
    );

    return response.data;
  }

  /** 일반 회원 탈퇴 (COMPANY_MANAGER · USER) — 7일 유예 후 삭제 */
  async withdrawMember(reason?: string): Promise<void> {
    const response = await httpClient.delete<Envelope<unknown>>(
      "/api/auth/withdraw",
      { data: { reason: reason || null } }
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "탈퇴에 실패했습니다.");
    }
  }
}

export const companyWithdrawalApiService = new CompanyWithdrawalApiService();
